import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Card } from "components/Dashboard/components";
import NoPermission from "components/Dashboard/components/NoPermission";
import { useGetFilterParams } from "components/Dashboard/hooks";
import { API_IS_BEING_CANCELLED, UNKNOWN } from "shared/constants/strings";
import { sortAndReturnArray } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { usePermissions } from "shared/hooks/usePermissions";
import { RectangleLoader } from "shared/loaders/RectangleLoader";
import { ENDPOINTS, FILTER_NAMES, ORDERS } from "shared/types/enums";

import { DATA_LIMIT } from "./constants";
import EmptyState from "./EmptyState";
import StatsContainer from "./StatsContainer";
import { Data, FirmwareConfigProps, TYPES } from "./types";

const FirmwareConfig = ({ type }: FirmwareConfigProps) => {
    const { t } = useTranslation();
    const { cancelSource, queryData, handleResponse, isCanceled } = useApi();
    const { stringifiedFilterParams } = useGetFilterParams();
    const { isReadDeviceNotAllowed } = usePermissions();
    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState(true);
    const [isMoreDataLoaded, setIsMoreDataLoaded] = useState(false);
    const [data, setData] = useState<Data[]>([]);

    const fields = {
        [TYPES.FirmwareVersions]: FILTER_NAMES.CurrentFw,
        [TYPES.Configurations]: FILTER_NAMES.CurrentCfg
    };

    const isDataEmpty = data.length === 0;

    const filterNames = {
        [TYPES.FirmwareVersions]: FILTER_NAMES.CurrentFw,
        [TYPES.Configurations]: FILTER_NAMES.CurrentCfg
    };

    const getStats = async (isLoadMoreData: boolean) => {
        try {
            !isLoadMoreData && setIsLoading(true);

            const limitFilter = isLoadMoreData ? `&limit=${DATA_LIMIT}` : "";

            const { data: allData } = await queryData(
                `${ENDPOINTS.DeviceStats}?field=${fields[type]}${limitFilter}`,
                stringifiedFilterParams
            );

            delete allData[UNKNOWN];

            const dataArray: Data[] = Object.values(allData);

            const sortedDataArray = sortAndReturnArray(
                dataArray,
                "count",
                ORDERS.Descending
            );

            if (isMounted) {
                setData(sortedDataArray);
                setIsMoreDataLoaded(isLoadMoreData);
            }
        } catch (error) {
            if (isMounted && !isCanceled(error)) handleResponse(error);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        !isReadDeviceNotAllowed && getStats(false);

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line
    }, [isReadDeviceNotAllowed, stringifiedFilterParams]);

    const renderContent = () => {
        if (isReadDeviceNotAllowed) {
            return (
                <NoPermission customStyle={{ margin: "0 30px 16px 30px" }} />
            );
        }

        if (isLoading) {
            return (
                <RectangleLoader
                    height={188}
                    customStyle={{ marginBottom: "116px" }}
                />
            );
        }

        return isDataEmpty ? (
            <EmptyState type={type} />
        ) : (
            <StatsContainer
                data={data}
                filterName={
                    filterNames[type] as
                        FILTER_NAMES.CurrentFw | FILTER_NAMES.CurrentCfg
                }
                isMoreDataLoaded={isMoreDataLoaded}
                loadMoreData={getStats}
            />
        );
    };

    return (
        <Card
            title={t(`Dashboard##title##${type}`)}
            infoTooltipText={
                !isReadDeviceNotAllowed
                    ? t(`Dashboard##tooltips##${type}`)
                    : undefined
            }
            containerCustomStyle={{
                height: "100%"
            }}
            headerCustomStyle={{
                minHeight: isReadDeviceNotAllowed ? "40px" : "unset"
            }}
            bodyCustomStyle={{
                ...(!isReadDeviceNotAllowed && !isLoading && !isDataEmpty
                    ? { paddingRight: "0" }
                    : {})
            }}
        >
            {renderContent()}
        </Card>
    );
};

export default FirmwareConfig;
export { TYPES as FIRMWARE_CONFIG_TYPES } from "./types";
